import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Typography,
  Card,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit, ArrowBack, Medication } from '@mui/icons-material';
import apiClient from '../lib/axios';

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

interface Dosage {
  id: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export const Dosages: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Dosage | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: dosages, isLoading } = useQuery({
    queryKey: ['dosages'],
    queryFn: async () => {
      const { data } = await apiClient.get<Dosage[]>('/products/dosages');
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (value: string) => {
      await apiClient.post('/products/dosages', { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosages'] });
      setCreateDialogOpen(false);
      setValue('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || t('catalog.errors.createDosage'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      await apiClient.put(`/products/dosages/${id}`, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosages'] });
      setEditDialogOpen(false);
      setSelected(null);
      setValue('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || t('catalog.errors.updateDosage'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/products/dosages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosages'] });
      setDeleteDialogOpen(false);
      setSelected(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || t('catalog.errors.deleteDosage'));
    },
  });

  const handleCreateClick = () => {
    setValue('');
    setError(null);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (dosage: Dosage) => {
    setSelected(dosage);
    setValue(dosage.value);
    setError(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (dosage: Dosage) => {
    setSelected(dosage);
    setError(null);
    setDeleteDialogOpen(true);
  };

  const allDosages = dosages ?? [];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.12)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.08)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                DOSIFICACIÓN
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              {t('catalog.dosages.title')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              {allDosages.length} dosis registradas
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ bgcolor: BLUE, flexShrink: 0, mt: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#0052a3' } }}
          >
            {isMobile ? 'Agregar' : t('catalog.dosages.add')}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {allDosages.length > 0 ? (
            allDosages.map((dosage) => (
              <Card key={dosage.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: `${BLUE}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: BLUE, flexShrink: 0 }}>
                      <Medication sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 15 }}>{dosage.value}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {new Date(dosage.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(dosage)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(dosage)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Medication sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>{t('catalog.dosages.empty')}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('catalog.dosages.valueLabel')}</TableCell>
                  <TableCell>{t('catalog.table.created')}</TableCell>
                  <TableCell align="right">{t('catalog.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allDosages.length > 0 ? (
                  allDosages.map((dosage) => (
                    <TableRow key={dosage.id}>
                      <TableCell>{dosage.value}</TableCell>
                      <TableCell>{new Date(dosage.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(dosage)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(dosage)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" py={4}>
                        {t('catalog.dosages.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('catalog.dosages.createTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('catalog.dosages.createDescription')}
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label={t('catalog.dosages.valueLabel')}
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={value.length > 0 && value.length < 2}
            helperText={value.length > 0 && value.length < 2 ? t('catalog.dosages.valueMinLength') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('catalog.buttons.cancel')}</Button>
          <Button
            onClick={() => createMutation.mutate(value.trim())}
            variant="contained"
            disabled={value.trim().length < 2 || createMutation.isPending}
          >
            {createMutation.isPending ? t('catalog.buttons.creating') : t('catalog.buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('catalog.dosages.editTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('catalog.dosages.editDescription')}
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label={t('catalog.dosages.valueLabel')}
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={value.length > 0 && value.length < 2}
            helperText={value.length > 0 && value.length < 2 ? t('catalog.dosages.valueMinLength') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('catalog.buttons.cancel')}</Button>
          <Button
            onClick={() => selected && updateMutation.mutate({ id: selected.id, value: value.trim() })}
            variant="contained"
            disabled={value.trim().length < 2 || updateMutation.isPending}
          >
            {updateMutation.isPending ? t('catalog.buttons.updating') : t('catalog.buttons.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('catalog.dosages.deleteTitle')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            {t('catalog.dosages.deleteConfirm')} <strong>{selected?.value}</strong>?
            {!error && ` ${t('catalog.dosages.deleteCannotUndo')}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('catalog.buttons.cancel')}</Button>
          <Button
            onClick={() => selected && deleteMutation.mutate(selected.id)}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('catalog.buttons.deleting') : t('catalog.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
